<script lang="ts" setup>
  import type { BackgroundStoryblok } from '@/storyblok/component-types-sb'
  import { SB_DEFAULT_IMAGE_OPTIONS, SB_IMAGE_PROVIDER } from '@/utils/constants/general'
  import { SCREEN_SM, SCREEN_MD } from '@/tailwind.config'
  import { optionalString } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: BackgroundStoryblok
  }>()

  const img = useImage()

  const bgImage = computed(() => {
    const mobileFilename = props.blok?.mobileBg?.filename
    const tabletFilename = props.blok?.tabletBg?.filename
    const desktopFilename = props.blok?.desktopBg?.filename

    const toUrl = (filename?: string | null) =>
      filename ? img(filename, SB_DEFAULT_IMAGE_OPTIONS, SB_IMAGE_PROVIDER) : ''

    const tabletUrl = toUrl(tabletFilename)
    const desktopUrl = toUrl(desktopFilename)

    return {
      mobileFilename: optionalString(mobileFilename),
      tabletUrl,
      desktopUrl
    }
  })
</script>

<template>
  <div class="relative">
    <picture
      class="pointer-events-none absolute inset-0 -z-10"
      aria-hidden="true"
    >
      <source
        v-if="bgImage.desktopUrl"
        :srcset="bgImage.desktopUrl"
        :media="`(min-width: ${SCREEN_MD}px)`"
      />
      <source
        v-if="bgImage.tabletUrl"
        :srcset="bgImage.tabletUrl"
        :media="`(min-width: ${SCREEN_SM}px)`"
      />
      <NuxtImg
        v-if="bgImage.mobileFilename"
        :src="bgImage.mobileFilename"
        provider="storyblok"
        :modifiers="SB_DEFAULT_IMAGE_OPTIONS"
        alt="Background image"
        role="presentation"
        aria-hidden="true"
        class="h-full w-full object-cover object-center"
        :class="{ 'rounded-md': blok.roundedCorners }"
        loading="eager"
      />
    </picture>
    <StoryblokComponent
      v-for="block in blok.body"
      :key="block._uid"
      :blok="block"
    />
  </div>
</template>
